import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  useGlobalStats, 
  useStudentStats, 
  usePaymentStatsDetailed, 
  useDossierStats,
  useExportReport
} from '../lib/hooks';
import { statsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { 
  Users, 
  BookOpen, 
  FolderCheck, 
  CreditCard, 
  TrendingUp, 
  TrendingDown,
  Handshake,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const formatCurrency = (amount, currency = 'XAF') => {
  return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + currency;
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('fr-FR').format(num);
};

// Color palette
const COLORS = {
  primary: '#cc007e',
  secondary: '#004aad',
  accent: '#eb350b',
  green: '#22c55e',
  orange: '#f59e0b',
  gray: '#6b7280',
};

const CHART_COLORS = ['#004aad', '#cc007e', '#22c55e', '#f59e0b', '#6b7280', '#8b5cf6'];

// KPI Card Component
const KPICard = ({ title, value, trend, trendValue, icon: Icon, color = 'secondary' }) => {
  const { lang } = useLanguage();
  const isPositive = trend === 'up';
  
  // Cap extreme percentages (e.g., when previous month is 0)
  const displayTrendValue = trendValue !== undefined && trendValue !== null 
    ? Math.min(Math.abs(trendValue), 999)
    : undefined;
  
  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold font-heading">{value}</p>
            {displayTrendValue !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{isPositive ? '+' : ''}{displayTrendValue}% {lang === 'de' ? 'vs. letzter Monat' : 'vs mois dernier'}</span>
              </div>
            )}
          </div>
          <div className={`w-14 h-14 rounded-2xl bg-${color}/10 flex items-center justify-center`} style={{ backgroundColor: `${COLORS[color]}15` }}>
            <Icon className="w-7 h-7" style={{ color: COLORS[color] }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Export Button Component
const ExportButton = ({ type, label, icon: Icon }) => {
  const [format, setFormat] = useState('excel');
  const { lang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const exportReport = useExportReport();
  
  const handleExport = async () => {
    try {
      const result = await exportReport.mutateAsync({ type, format, filters: {} });
      
      // Download the file
      const downloadUrl = statsApi.getReportDownloadUrl(result.filename);
      window.open(downloadUrl, '_blank');
      
      toast.success(lang === 'de' ? `Bericht "${label}" exportiert` : `Rapport ${label.toLowerCase()} exporté`);
      setIsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Export' : 'Erreur lors de l\'export'));
    }
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-24 flex-col gap-2" data-testid={`export-${type}-btn`}>
          <Icon className="w-6 h-6 text-secondary" />
          <span className="text-sm">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <div className="space-y-4">
          <h4 className="font-medium">{lang === 'de' ? 'Exportformat' : 'Format d\'export'}</h4>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="excel">
                <span className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" /> Excel (.xlsx)
                </span>
              </SelectItem>
              <SelectItem value="pdf">
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" /> PDF
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={handleExport} 
            className="w-full bg-secondary hover:bg-secondary/90"
            disabled={exportReport.isPending}
          >
            {exportReport.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {lang === 'de' ? 'Bericht erstellen' : 'Générer le rapport'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const StatisticsPage = () => {
  const { lang } = useLanguage();
  const { data: globalStats, isLoading: globalLoading, refetch: refetchGlobal } = useGlobalStats();
  const { data: studentStats, isLoading: studentLoading } = useStudentStats();
  const { data: paymentStats, isLoading: paymentLoading } = usePaymentStatsDetailed();
  const { data: dossierStats, isLoading: dossierLoading } = useDossierStats();
  
  const isLoading = globalLoading || studentLoading || paymentLoading || dossierLoading;
  
  const handleRefresh = () => {
    refetchGlobal();
    toast.success(lang === 'de' ? 'Statistiken aktualisiert' : 'Statistiques actualisées');
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-secondary mx-auto mb-4" />
          <p className="text-muted-foreground">{lang === 'de' ? 'Statistiken werden geladen...' : 'Chargement des statistiques...'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-8 animate-fade-in" data-testid="statistics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">{lang === 'de' ? 'Statistiken & Berichte' : 'Statistiques & Rapports'}</h1>
          <p className="text-muted-foreground mt-1">
            {lang === 'de' ? 'Letzte Aktualisierung' : 'Dernière mise à jour'}: {new Date().toLocaleString(lang === 'de' ? 'de-DE' : 'fr-FR')}
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} data-testid="refresh-stats-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          {lang === 'de' ? 'Aktualisieren' : 'Actualiser'}
        </Button>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard
          title={lang === 'de' ? 'Aktive Studierende' : 'Étudiants actifs'}
          value={formatNumber(globalStats?.students?.active || 0)}
          trend={globalStats?.students?.growth_rate >= 0 ? 'up' : 'down'}
          trendValue={Math.abs(globalStats?.students?.growth_rate || 0)}
          icon={Users}
          color="secondary"
        />
        <KPICard
          title={lang === 'de' ? 'Aktive Kurse' : 'Cours actifs'}
          value={formatNumber(globalStats?.courses?.active || 0)}
          icon={BookOpen}
          color="primary"
        />
        <KPICard
          title={lang === 'de' ? 'Vollständige Akten' : 'Dossiers complets'}
          value={formatNumber(globalStats?.dossiers?.complete || 0)}
          icon={FolderCheck}
          color="green"
        />
        <KPICard
          title={lang === 'de' ? 'Diesen Monat eingenommen' : 'Encaissé ce mois'}
          value={formatCurrency(globalStats?.payments?.collected_this_month || 0)}
          trend={globalStats?.payments?.collected_this_month > globalStats?.payments?.collected_last_month ? 'up' : 'down'}
          trendValue={Math.round(
            ((globalStats?.payments?.collected_this_month - globalStats?.payments?.collected_last_month) / 
            Math.max(globalStats?.payments?.collected_last_month, 1)) * 100
          )}
          icon={CreditCard}
          color="secondary"
        />
        <KPICard
          title={lang === 'de' ? 'Überfällig' : 'En retard'}
          value={formatCurrency(globalStats?.payments?.total_overdue || 0)}
          icon={AlertCircle}
          color="accent"
        />
        <KPICard
          title={lang === 'de' ? 'Aktive Partner' : 'Partenaires actifs'}
          value={formatNumber(globalStats?.partners?.active || 0)}
          icon={Handshake}
          color="primary"
        />
      </div>
      
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Registrations Line Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Neue Einschreibungen' : 'Nouvelles inscriptions'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Letzte 12 Monate' : '12 derniers mois'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={studentStats?.monthly_registrations || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(value) => [value, lang === 'de' ? 'Einschreibungen' : 'Inscriptions']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke={COLORS.primary} 
                    strokeWidth={3}
                    dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Level Distribution Pie Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Verteilung nach Niveau' : 'Répartition par niveau'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Verteilung der Studierenden' : 'Distribution des étudiants'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center">
              <ResponsiveContainer width="60%" height="100%">
                <PieChart>
                  <Pie
                    data={studentStats?.level_distribution || []}
                    dataKey="count"
                    nameKey="level"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ level, percent }) => `${level} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#6b7280' }}
                  >
                    {(studentStats?.level_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {(studentStats?.level_distribution || []).map((item, index) => (
                  <div key={item.level} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                    <span className="text-sm">{item.level}: {item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payments Bar Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Eingegangene Zahlungen' : 'Paiements encaissés'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Letzte 12 Monate' : '12 derniers mois'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={paymentStats?.monthly_collected || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `${(value/1000)}k`} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(value) => [formatCurrency(value), lang === 'de' ? 'Eingenommen' : 'Encaissé']}
                  />
                  <Bar dataKey="amount" fill={COLORS.secondary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Dossier Status Donut Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Aktenstatus' : 'Statut des dossiers'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Aktuelle Verteilung' : 'Répartition actuelle'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center justify-center">
              <ResponsiveContainer width="60%" height="100%">
                <PieChart>
                  <Pie
                    data={dossierStats?.status_distribution || []}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    label={({ status, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {(dossierStats?.status_distribution || []).map((entry, index) => {
                      const colors = [COLORS.green, COLORS.orange, COLORS.gray];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {(dossierStats?.status_distribution || []).map((item, index) => {
                  const colors = [COLORS.green, COLORS.orange, COLORS.gray];
                  const statusLabel = lang === 'de'
                    ? ({ complete: 'Vollständig', in_progress: 'In Bearbeitung', incomplete: 'Unvollständig', pdf_generated: 'PDF erstellt' }[item.status] || item.status)
                    : ({ complete: 'Complet', in_progress: 'En cours', incomplete: 'Incomplet', pdf_generated: 'PDF généré' }[item.status] || item.status);
                  return (
                    <div key={item.status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[index % colors.length] }} />
                        <span className="text-sm">{statusLabel}</span>
                      </div>
                      <Badge variant="outline">{item.count}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visa Objectives Bar Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Visumziele' : 'Objectifs visa'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Verteilung der Studierenden' : 'Répartition des étudiants'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={studentStats?.visa_distribution || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="type" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="count" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Payment Methods Pie Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Zahlungsmethoden' : 'Modes de paiement'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Verteilung der Einnahmen' : 'Répartition des encaissements'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="60%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentStats?.payment_methods || []}
                    dataKey="amount"
                    nameKey="method"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    label={({ method, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {(paymentStats?.payment_methods || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={[COLORS.secondary, COLORS.primary, COLORS.accent][index % 3]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {(paymentStats?.payment_methods || []).map((item, index) => {
                  const colors = [COLORS.secondary, COLORS.primary, COLORS.accent];
                  return (
                    <div key={item.method} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[index % 3] }} />
                      <div>
                        <p className="text-sm font-medium">{item.method}</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(item.amount)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Rejected Documents */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Am häufigsten abgelehnte Dokumente' : 'Documents les plus rejetés'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Top 5 der Dokumenttypen' : 'Top 5 des types de documents'}</CardDescription>
          </CardHeader>
          <CardContent>
            {(dossierStats?.most_rejected?.length || 0) === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {lang === 'de' ? 'Keine abgelehnten Dokumente' : 'Aucun document rejeté'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{lang === 'de' ? 'Dokumenttyp' : 'Type de document'}</TableHead>
                    <TableHead className="text-right">{lang === 'de' ? 'Ablehnungen' : 'Rejets'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(dossierStats?.most_rejected || []).map((item) => (
                    <TableRow key={item.type}>
                      <TableCell className="font-medium">{item.type}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="destructive">{item.count}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        {/* Overdue Payments */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{lang === 'de' ? 'Überfällige Zahlungen' : 'Paiements en retard'}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Top 10 der Studierenden' : 'Top 10 des étudiants'}</CardDescription>
          </CardHeader>
          <CardContent>
            {(paymentStats?.overdue_students?.length || 0) === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {lang === 'de' ? 'Keine überfälligen Zahlungen' : 'Aucun paiement en retard'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{lang === 'de' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                    <TableHead className="text-right">{lang === 'de' ? 'Betrag' : 'Montant'}</TableHead>
                    <TableHead className="text-right">{lang === 'de' ? 'Tage' : 'Jours'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(paymentStats?.overdue_students || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-right text-red-600 font-medium">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">{item.days_overdue}j</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Export Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">{lang === 'de' ? 'Bericht exportieren' : 'Exporter un rapport'}</CardTitle>
          <CardDescription>{lang === 'de' ? 'Detaillierte Berichte als PDF oder Excel erstellen' : 'Générez des rapports détaillés en PDF ou Excel'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <ExportButton type="students" label={lang === 'de' ? 'Studierende' : 'Rapport étudiants'} icon={Users} />
            <ExportButton type="payments" label={lang === 'de' ? 'Zahlungen' : 'Rapport paiements'} icon={CreditCard} />
            <ExportButton type="dossiers" label={lang === 'de' ? 'Akten' : 'Rapport dossiers'} icon={FolderCheck} />
            <ExportButton type="courses" label={lang === 'de' ? 'Kurse' : 'Rapport cours'} icon={BookOpen} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatisticsPage;
